// Billing reports as spreadsheet files (CSV). The annual one is for the pengurus's
// yearly accountability report (LPJ): one row per house, January to December, then
// the year's totals. The monthly one lists a single month per house. Both are
// semicolon-separated with a byte-order mark, which is what Excel in an Indonesian
// locale opens correctly by double-click.

import type { PublicInvoice } from '@residenone/shared';
import type { UnitBilling } from './ops-api';

export interface AnnualCsvLabels {
  house: string;
  billed: string;
  paid: string;
  owed: string;
  total: string;
  /** Twelve short month names, January first. */
  months: string[];
  status: { PAID: string; UNPAID: string; NONE: string };
}

const SEP = ';';
const isUnpaid = (i: PublicInvoice) => i.status === 'OPEN' || i.status === 'OVERDUE';
const rupiah = (cents: number) => String(Math.round(cents / 100));

function cell(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Rows of cells as a CSV file in the format above (BOM, semicolons, CRLF). */
export function csvFile(rows: string[][]): string {
  return '\uFEFF' + rows.map((r) => r.map(cell).join(SEP)).join('\r\n') + '\r\n';
}

export function buildAnnualCsv(units: UnitBilling[], year: number, labels: AnnualCsvLabels): string {
  const months = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
  const collectedPerMonth = months.map(() => 0);
  let totalBilled = 0;
  let totalPaid = 0;

  const sorted = [...units].sort(
    (a, b) => a.block.localeCompare(b.block) || a.number.localeCompare(b.number, undefined, { numeric: true }),
  );

  const rows = sorted.map((u) => {
    const ofYear = u.invoices.filter((i) => i.status !== 'VOID' && i.issue_date.startsWith(`${year}-`));
    let billed = 0;
    let paid = 0;
    const statuses = months.map((m, idx) => {
      const ofMonth = ofYear.filter((i) => i.issue_date.startsWith(m));
      for (const i of ofMonth) {
        if (i.status === 'PAID') {
          billed += i.total_cents;
          paid += i.total_cents;
          collectedPerMonth[idx]! += i.total_cents;
        } else if (isUnpaid(i)) {
          billed += i.total_cents;
          paid += i.paid_cents;
        }
      }
      const counted = ofMonth.filter((i) => i.status === 'PAID' || isUnpaid(i));
      if (counted.length === 0) return labels.status.NONE;
      return counted.some(isUnpaid) ? labels.status.UNPAID : labels.status.PAID;
    });
    totalBilled += billed;
    totalPaid += paid;
    return [u.label, ...statuses, rupiah(billed), rupiah(paid), rupiah(billed - paid)];
  });

  const header = [labels.house, ...labels.months, labels.billed, labels.paid, labels.owed];
  const totals = [labels.total, ...collectedPerMonth.map(rupiah), rupiah(totalBilled), rupiah(totalPaid), rupiah(totalBilled - totalPaid)];
  return csvFile([header, ...rows, totals]);
}

export interface MonthlyCsvLabels {
  house: string;
  status: string;
  billed: string;
  paid: string;
  owed: string;
  paidOn: string;
  total: string;
  statuses: { PAID: string; UNPAID: string; NONE: string };
}

/**
 * One month as a spreadsheet: a row per house with that month's status, money
 * billed, paid and still owed, and the payment date, then the month's totals.
 * Same format as the annual file. `month` is 'YYYY-MM'.
 */
export function buildMonthlyCsv(units: UnitBilling[], month: string, labels: MonthlyCsvLabels): string {
  let totalBilled = 0;
  let totalPaid = 0;

  const sorted = [...units].sort(
    (a, b) => a.block.localeCompare(b.block) || a.number.localeCompare(b.number, undefined, { numeric: true }),
  );

  const rows = sorted.map((u) => {
    const counted = u.invoices.filter((i) => i.issue_date.startsWith(month) && (i.status === 'PAID' || isUnpaid(i)));
    const billed = counted.reduce((s, i) => s + i.total_cents, 0);
    const paid = counted.reduce((s, i) => s + (i.status === 'PAID' ? i.total_cents : i.paid_cents), 0);
    totalBilled += billed;
    totalPaid += paid;
    const status = counted.length === 0
      ? labels.statuses.NONE
      : counted.some(isUnpaid) ? labels.statuses.UNPAID : labels.statuses.PAID;
    // The latest payment date, once nothing is left to pay.
    const paidOn = counted.length > 0 && !counted.some(isUnpaid)
      ? counted.map((i) => i.paid_at?.slice(0, 10) ?? '').sort().at(-1) ?? ''
      : '';
    return [u.label, status, rupiah(billed), rupiah(paid), rupiah(billed - paid), paidOn];
  });

  const header = [labels.house, labels.status, labels.billed, labels.paid, labels.owed, labels.paidOn];
  const totals = [labels.total, '', rupiah(totalBilled), rupiah(totalPaid), rupiah(totalBilled - totalPaid), ''];
  return csvFile([header, ...rows, totals]);
}

/** Hands the CSV to the browser as a file download. */
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
